/** Host growth focus, checklist, and path-tour copy — single owner for web and mobile. */

export const GROWTH_FOCUSES = ['topic', 'marketplace', 'business_team', 'topic_shop'] as const;
export type GrowthFocus = (typeof GROWTH_FOCUSES)[number];

export const GROWTH_FOCUS_LABELS: Record<GrowthFocus, string> = {
  topic: 'Topic community',
  marketplace: 'Marketplace storefront',
  business_team: 'Business with an internal team',
  topic_shop: 'Topic community that also sells',
};

export const GROWTH_FOCUS_LEDES: Record<GrowthFocus, string> = {
  topic:
    'Bring people together around a subject. Use the feed, a blog post, groups, and the calendar, then invite members.',
  marketplace:
    'The community is your shop. List it in the directory, take payments as guests, and invite only the people who run the store.',
  business_team:
    'Show the business in the directory and shop. Customers pay as guests. The crew uses a private group and the calendar.',
  topic_shop:
    'A topic community that also sells. Members discuss the subject; shoppers who only buy stay guests.',
};

export function isGrowthFocus(value: string): value is GrowthFocus {
  return (GROWTH_FOCUSES as readonly string[]).includes(value);
}

/** Empty string is unset. Invalid values return null. */
export function parseGrowthFocus(raw: unknown): GrowthFocus | '' | null {
  if (raw == null) return '';
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  if (v === '') return '';
  return isGrowthFocus(v) ? v : null;
}

export type GrowthStepId =
  | 'join_policy'
  | 'directory'
  | 'feed'
  | 'blog'
  | 'groups'
  | 'calendar'
  | 'invites'
  | 'marketplace'
  | 'seller'
  | 'vendor_approve';

export type GrowthDoneRule = 'directory' | 'marketplace' | 'blog' | 'link';

export type GrowthStep = {
  id: GrowthStepId;
  title: string;
  body: string;
  doneRule: GrowthDoneRule;
};

export type GrowthGuideSnapshot = {
  directoryListed?: boolean;
  displayName?: string;
  marketplaceEnabled?: boolean;
  publishedBlogCount?: number;
};

export function growthStepDone(step: GrowthStep, snap: GrowthGuideSnapshot): boolean {
  switch (step.doneRule) {
    case 'directory':
      return !!snap.directoryListed && (snap.displayName ?? '').trim().length > 0;
    case 'marketplace':
      return !!snap.marketplaceEnabled;
    case 'blog':
      return (snap.publishedBlogCount ?? 0) > 0;
    default:
      return false;
  }
}

export function seatLimitCopy(seatCap: number, memberCount: number): string {
  const members = Number.isFinite(memberCount) ? Math.max(0, Math.floor(memberCount)) : 0;
  const cap = Number.isFinite(seatCap) ? Math.floor(seatCap) : 0;
  if (cap <= 0) {
    return `You have ${members} registered members. Guest shoppers do not use a seat.`;
  }
  return `You have ${members} of ${cap} member seats. Guest shoppers do not use a seat.`;
}

export function inviteStepBody(focus: GrowthFocus, seatCap: number, memberCount: number): string {
  const seats = seatLimitCopy(seatCap, memberCount);
  switch (focus) {
    case 'marketplace':
      return `Invite only the people who run the shop. Customers pay as guests and do not become members. ${seats}`;
    case 'business_team':
      return `Invite the crew, not each customer. Customers pay as guests. ${seats}`;
    case 'topic_shop':
      return `Invite the people in the topic. Shoppers who only buy stay guests. ${seats}`;
    default:
      return `Invite members to this community. ${seats}`;
  }
}

function joinPolicyStep(focus: GrowthFocus): GrowthStep {
  const portal =
    'Approve pending requests on the host portal on the web. Mobile does not show a request queue.';
  switch (focus) {
    case 'marketplace':
      return {
        id: 'join_policy',
        title: 'Who can join',
        body: `Keep join policy invite-only so customers are not asked to become members. ${portal}`,
        doneRule: 'link',
      };
    case 'business_team':
      return {
        id: 'join_policy',
        title: 'Who can join',
        body: `Keep join policy invite-only. The crew are members; customers pay as guests. ${portal}`,
        doneRule: 'link',
      };
    case 'topic_shop':
      return {
        id: 'join_policy',
        title: 'Who can join',
        body: `Members are the people in the topic. Shoppers who only buy stay guests. ${portal}`,
        doneRule: 'link',
      };
    default:
      return {
        id: 'join_policy',
        title: 'Who can join',
        body: `Choose invite only, open, or approval. ${portal}`,
        doneRule: 'link',
      };
  }
}

const DIRECTORY_STEP: GrowthStep = {
  id: 'directory',
  title: 'List in the directory',
  body: 'A public name and short blurb help people find this community. Anyone can still open it by slug.',
  doneRule: 'directory',
};

const FEED_STEP: GrowthStep = {
  id: 'feed',
  title: 'Start a conversation',
  body: 'The Feed and Forum are the same board. Post once here to start the conversation.',
  doneRule: 'link',
};

const BLOG_STEP: GrowthStep = {
  id: 'blog',
  title: 'Write a blog post',
  body: 'Publish a post that explains the topic. Drafts stay private until you publish.',
  doneRule: 'blog',
};

const GROUPS_STEP: GrowthStep = {
  id: 'groups',
  title: 'Create a group',
  body: 'A group’s join rules are separate from who can join the community. Create starts as open unless you change it.',
  doneRule: 'link',
};

const PRIVATE_GROUP_STEP: GrowthStep = {
  id: 'groups',
  title: 'Create a private crew group',
  body: 'Choose Private when you create the group so the crew can talk without making that circle public.',
  doneRule: 'link',
};

const CALENDAR_STEP: GrowthStep = {
  id: 'calendar',
  title: 'Add a calendar event',
  body: 'Hosts and calendar editors can create events. Members can suggest dates for you to approve.',
  doneRule: 'link',
};

const CREW_CALENDAR_STEP: GrowthStep = {
  id: 'calendar',
  title: 'Put the crew on the calendar',
  body: 'Use the calendar for the team schedule. Customers do not need a seat to pay.',
  doneRule: 'link',
};

const MARKETPLACE_STEP: GrowthStep = {
  id: 'marketplace',
  title: 'Turn on the marketplace',
  body: 'Activate shops from Settings on the web or You on mobile. Buyers can check out as guests.',
  doneRule: 'marketplace',
};

const SELLER_STEP: GrowthStep = {
  id: 'seller',
  title: 'Set up the seller dashboard',
  body: 'Accept site terms and marketplace terms, finish Stripe payment setup, then apply for a storefront.',
  doneRule: 'link',
};

const VENDOR_APPROVE_STEP: GrowthStep = {
  id: 'vendor_approve',
  title: 'Approve the storefront',
  body: 'Open Admin → Vendor applications on the web. Approve stays off until Stripe payment setup is complete.',
  doneRule: 'link',
};

function invitesStep(focus: GrowthFocus): GrowthStep {
  return {
    id: 'invites',
    title: 'Invite people',
    body: inviteStepBody(focus, 0, 0),
    doneRule: 'link',
  };
}

const SHOP_STEPS: GrowthStep[] = [MARKETPLACE_STEP, SELLER_STEP, VENDOR_APPROVE_STEP];

export function checklistSteps(focus: GrowthFocus): GrowthStep[] {
  switch (focus) {
    case 'topic':
      return [joinPolicyStep(focus), DIRECTORY_STEP, FEED_STEP, BLOG_STEP, GROUPS_STEP, CALENDAR_STEP, invitesStep(focus)];
    case 'marketplace':
      return [joinPolicyStep(focus), DIRECTORY_STEP, ...SHOP_STEPS, invitesStep(focus)];
    case 'business_team':
      return [
        joinPolicyStep(focus),
        DIRECTORY_STEP,
        ...SHOP_STEPS,
        PRIVATE_GROUP_STEP,
        CREW_CALENDAR_STEP,
        invitesStep(focus),
      ];
    case 'topic_shop':
      return [
        joinPolicyStep(focus),
        DIRECTORY_STEP,
        FEED_STEP,
        BLOG_STEP,
        GROUPS_STEP,
        CALENDAR_STEP,
        ...SHOP_STEPS,
        invitesStep(focus),
      ];
  }
}

/** Unset focus shows the picker only. */
export function checklistForSavedFocus(focus: GrowthFocus | ''): GrowthStep[] {
  return focus ? checklistSteps(focus) : [];
}

export function withInviteSeatCopy(steps: GrowthStep[], focus: GrowthFocus, seatCap: number, memberCount: number): GrowthStep[] {
  return steps.map((step) =>
    step.id === 'invites' ? { ...step, body: inviteStepBody(focus, seatCap, memberCount) } : step,
  );
}

export type PathTourStopId =
  | 'directory'
  | 'feed'
  | 'blog'
  | 'groups'
  | 'calendar'
  | 'invites'
  | 'marketplace_enable'
  | 'seller'
  | 'vendor_approve'
  | 'create_group';

export type PathTourStop = {
  id: PathTourStopId;
  title: string;
  body: string;
};

function tourFromChecklist(step: GrowthStep, id: PathTourStopId): PathTourStop {
  return { id, title: step.title, body: step.body };
}

const TOPIC_TOUR: PathTourStop[] = [
  tourFromChecklist(DIRECTORY_STEP, 'directory'),
  tourFromChecklist(FEED_STEP, 'feed'),
  tourFromChecklist(BLOG_STEP, 'blog'),
  tourFromChecklist(GROUPS_STEP, 'groups'),
  tourFromChecklist(CALENDAR_STEP, 'calendar'),
  tourFromChecklist(invitesStep('topic'), 'invites'),
];

const SHOP_TOUR: PathTourStop[] = [
  tourFromChecklist(MARKETPLACE_STEP, 'marketplace_enable'),
  tourFromChecklist(SELLER_STEP, 'seller'),
  tourFromChecklist(VENDOR_APPROVE_STEP, 'vendor_approve'),
];

export function pathTourStops(focus: GrowthFocus): PathTourStop[] {
  switch (focus) {
    case 'topic':
      return TOPIC_TOUR;
    case 'marketplace':
      return [
        tourFromChecklist(DIRECTORY_STEP, 'directory'),
        ...SHOP_TOUR,
        tourFromChecklist(invitesStep('marketplace'), 'invites'),
      ];
    case 'business_team':
      return [
        tourFromChecklist(DIRECTORY_STEP, 'directory'),
        ...SHOP_TOUR,
        tourFromChecklist(PRIVATE_GROUP_STEP, 'create_group'),
        tourFromChecklist(CREW_CALENDAR_STEP, 'calendar'),
        tourFromChecklist(invitesStep('business_team'), 'invites'),
      ];
    case 'topic_shop':
      return [
        ...TOPIC_TOUR.slice(0, -1),
        ...SHOP_TOUR,
        tourFromChecklist(invitesStep('topic_shop'), 'invites'),
      ];
  }
}

export function growthTourPersistenceTrack(focus: GrowthFocus): string {
  return `growth-${focus}`;
}

export function growthTourDoneStorageKey(focus: GrowthFocus, slug: string): string {
  const normalized = slug.trim().toLowerCase() || 'default';
  return `communio:nav-tour:${growthTourPersistenceTrack(focus)}:done:${normalized}`;
}

export const TAKE_GROWTH_TOUR_LABEL = 'Take the tour';
export const GROWTH_FOCUS_SAVED = 'Focus saved.';
export const HOST_HELP_RETRY = 'Try again';

export const HOST_HELP_FEED_CARD_TITLE = 'Grow this community';
export const HOST_HELP_FEED_CARD_BODY = 'Pick how you want to use Communio, then follow a short guide.';

export function growthTourStepIdFromPathname(pathname: string | null | undefined): PathTourStopId | null {
  if (!pathname) return null;
  const raw = pathname.split('?')[0].split('#')[0];
  const parts = raw.split('/').filter((p) => p && p !== '(app)' && p !== '(tabs)' && p !== 'tour');
  const joined = parts.join('/');
  if (parts.includes('help')) return 'directory';
  if (joined.includes('settings/invite') || parts.includes('invite-members')) return 'invites';
  if (joined.includes('settings/account') || parts.includes('you')) return 'marketplace_enable';
  if (parts.includes('admin')) return 'vendor_approve';
  if (joined.includes('marketplace/sell') || (parts.includes('marketplace') && parts.includes('sell'))) {
    return 'seller';
  }
  if (parts.includes('compose-blog') || (parts.includes('blog') && parts.includes('compose'))) return 'blog';
  if (parts.includes('blog')) return 'blog';
  if (joined.includes('groups/create') || parts.includes('create-group')) return 'create_group';
  if (parts.includes('groups')) return 'groups';
  if (parts.includes('calendar')) return 'calendar';
  if (parts.length === 0 || parts[0] === 'feed' || parts[parts.length - 1] === 'home') return 'feed';
  return null;
}

export function growthTourStepIndexForPathname(
  pathname: string | null | undefined,
  stepIds: readonly string[],
): number | null {
  const id = growthTourStepIdFromPathname(pathname);
  if (!id) return null;
  const idx = stepIds.indexOf(id);
  return idx >= 0 ? idx : null;
}

export type GrowthStepHref = { web: string; mobile: string };

export const GROWTH_STEP_HREFS: Record<GrowthStepId, GrowthStepHref> = {
  join_policy: { web: 'https://communiosocial.com/saas/portal/', mobile: '/(app)/you' },
  directory: { web: '/help/', mobile: '/(app)/help' },
  feed: { web: '/', mobile: '/(app)/(tabs)/home' },
  blog: { web: '/blog/compose/', mobile: '/compose-blog' },
  groups: { web: '/groups/create/', mobile: '/(app)/create-group' },
  calendar: { web: '/calendar/', mobile: '/(app)/(tabs)/calendar' },
  invites: { web: '/settings/invite/', mobile: '/(app)/invite-members' },
  marketplace: { web: '/settings/account/', mobile: '/(app)/you' },
  seller: { web: '/marketplace/sell/', mobile: '/(app)/(tabs)/marketplace/sell' },
  vendor_approve: { web: '/admin/', mobile: '/(app)/you' },
};

export const GROWTH_TOUR_HREFS: Record<PathTourStopId, GrowthStepHref> = {
  directory: GROWTH_STEP_HREFS.directory,
  feed: GROWTH_STEP_HREFS.feed,
  blog: { web: '/blog/', mobile: '/(app)/(tabs)/blog' },
  groups: { web: '/groups/', mobile: '/(app)/(tabs)/groups' },
  calendar: GROWTH_STEP_HREFS.calendar,
  invites: GROWTH_STEP_HREFS.invites,
  marketplace_enable: GROWTH_STEP_HREFS.marketplace,
  seller: GROWTH_STEP_HREFS.seller,
  vendor_approve: GROWTH_STEP_HREFS.vendor_approve,
  create_group: GROWTH_STEP_HREFS.groups,
};
